import * as React from 'react'
// libs
import { useSelector } from 'react-redux'
import { getFormValues } from 'redux-form'
import { useMutation } from '@apollo/client/react'
// components
import { Button, BUTTON_LAYOUTS, Modal } from 'components/UI'
import { CloseIcon } from 'components/UI/Icons'
import Select from 'components/FormElements/Select'
import EntityDefaultsForm from './components/EntityForm'
// hooks
import { useModalData } from 'hooks/index'
// constants
import {
  Modals,
  REPEAT_OPTIONS,
  PLAN_SCHEDULE_FLAT_SECTIONS,
  EACH_PERIOD_ENDS_TYPE_OPTIONS,
  PLAN_SCHEDULE_ENTITY_TYPE_VALUES,
  PLAN_SCHEDULE_OBJECTIVE_FORM_PREFIX
} from 'constants/index'
import { REVIEW_REPEAT_OPTIONS, REVIEW_TYPE_OPTIONS } from 'constants/review'
// graphql
import { PLAN_SCHEDULE_DEFAULTS_SET_MUTATION } from 'graphql/plan/mutations'
// views
import { ButtonWrapper, Header, LevelSelectWrapper, Paper, Tab, TabPanel, Tabs } from './views'
// utils
import { handleError } from 'utils/errors/handlers'
import { getOptionByValue, getValueFromOption } from 'utils/index'
// types
import { IPlanScheduleDefault } from 'types/graphql'

const PAPER_PROPS = {
  style: {
    margin: 0,
    overflow: 'auto',
    position: 'fixed' as any,
    maxWidth: '472px'
  }
}

const OBJECTIVES_TAB = 'objective'

// scheduleDefaults entry -> flat form values (same field names as the review form)
export const toFormValues = (scheduleDefault: IPlanScheduleDefault) => {
  if (!scheduleDefault) {
    return {}
  }

  const reviewDate = scheduleDefault.reviewDate || {}

  return {
    repeating: scheduleDefault.repeating,
    repeatEveryValue: scheduleDefault.repeatEveryValue,
    repeatEveryType: getOptionByValue(scheduleDefault.repeatEveryType, REPEAT_OPTIONS),
    periodEndsOn: getOptionByValue(scheduleDefault.periodEndsOn, EACH_PERIOD_ENDS_TYPE_OPTIONS),
    startPeriod: scheduleDefault.startPeriod,
    endPeriod: scheduleDefault.repeating ? undefined : scheduleDefault.endPeriod,
    firstPeriodEndsAt: scheduleDefault.repeating ? scheduleDefault.endPeriod : undefined,
    recurrenceEnds: scheduleDefault.endsOn,
    recurrenceEndsAt: scheduleDefault.endsOnDate,
    completionDate: scheduleDefault.repeating ? undefined : reviewDate.date,
    end: getOptionByValue(reviewDate.end, REVIEW_REPEAT_OPTIONS),
    value: reviewDate.value,
    period: getOptionByValue(reviewDate.period, REVIEW_TYPE_OPTIONS)
  }
}

// flat form values (+ entityType/levelId) -> PlanScheduleDefaultInput
const toInput = (values = {} as any, entityType: string, levelId: string | null) => ({
  entityType,
  levelId: levelId || null,
  repeating: !!values.repeating,
  repeatEveryValue:
    values.repeatEveryValue === '' || values.repeatEveryValue == null ? null : parseFloat(values.repeatEveryValue),
  repeatEveryType: getValueFromOption(values.repeatEveryType) || null,
  periodEndsOn: getValueFromOption(values.periodEndsOn) || null,
  startPeriod: values.startPeriod || null,
  endPeriod: (values.repeating ? values.firstPeriodEndsAt : values.endPeriod) || null,
  endsOn: values.recurrenceEnds || null,
  endsOnDate: values.recurrenceEndsAt || null,
  reviewDate: {
    date: values.completionDate || null,
    end: getValueFromOption(values.end) || null,
    value: values.value === '' || values.value == null ? null : parseFloat(values.value),
    period: getValueFromOption(values.period) || null
  }
})

const PlanScheduleDefaultsModal: React.FC = () => {
  const [setScheduleDefaults, { loading: isSaving }] = useMutation(PLAN_SCHEDULE_DEFAULTS_SET_MUTATION, {
    update: (cache, _result, { variables }) => {
      if (!variables) {
        return
      }

      cache.modify({
        id: cache.identify({ __typename: 'Plan', id: variables.planId }),
        fields: {
          scheduleDefaults: () =>
            variables.input.map((entry) => ({
              __typename: 'PlanScheduleDefault',
              ...entry,
              reviewDate: entry.reviewDate ? { __typename: 'ScheduledReviewersDate', ...entry.reviewDate } : null
            }))
        }
      })
    }
  })

  const [activeTab, setActiveTab] = React.useState<string>(OBJECTIVES_TAB)
  const [selectedLevel, setSelectedLevel] = React.useState<{ label: string; value: string }>(null)

  const {
    hideModal,
    modalData: { isOpen, plan }
  } = useModalData({ modalType: Modals.PLAN_SCHEDULE_DEFAULTS_MODAL })

  const scheduleDefaults = plan?.scheduleDefaults || []
  const objectiveLevels = [...(plan?.planType?.levels || [])].sort((a, b) => a.position - b.position)
  const levelOptions = objectiveLevels.map((level) => ({ label: level.description, value: level.id }))
  const currentLevel = levelOptions.find(({ value }) => value === selectedLevel?.value) || levelOptions[0]

  // every redux-form instance behind the modal (one per objective level + one per flat section)
  const forms = [
    ...objectiveLevels.map((level) => ({
      form: `${PLAN_SCHEDULE_OBJECTIVE_FORM_PREFIX}_${level.id}`,
      entityType: PLAN_SCHEDULE_ENTITY_TYPE_VALUES.OBJECTIVE,
      levelId: level.id
    })),
    ...PLAN_SCHEDULE_FLAT_SECTIONS.map((section) => ({
      form: section.form,
      entityType: section.entityType,
      levelId: null
    }))
  ]

  const formValuesByForm = useSelector((state) =>
    forms.reduce(
      (acc, { form }) => {
        acc[form] = getFormValues(form)(state) || {}
        return acc
      },
      {} as Record<string, any>
    )
  )

  const matchDefault = (entityType: string, levelId: string | null) =>
    scheduleDefaults.find(
      (item: IPlanScheduleDefault) => item.entityType === entityType && (!levelId || item.levelId === levelId)
    )

  const handleSave = async () => {
    const input = forms.map(({ entityType, levelId, form }) => toInput(formValuesByForm[form], entityType, levelId))

    try {
      await setScheduleDefaults({
        variables: { planId: plan.id, input }
      })
      hideModal()
    } catch (error) {
      handleError(error, 'PlanScheduleDefaultsModal.handleSave')
    }
  }

  return (
    <Modal
      width={'xs'}
      isOpen={isOpen}
      disableBackdropClick
      disableEscapeKeyDown
      hideModal={hideModal}
      PaperProps={PAPER_PROPS}>
      <Paper>
        <Header>
          <span>Schedule Defaults</span>
          <CloseIcon right={0} onClick={hideModal} />
        </Header>

        <Tabs>
          <Tab $active={activeTab === OBJECTIVES_TAB} onClick={() => setActiveTab(OBJECTIVES_TAB)}>
            Objectives
          </Tab>
          {PLAN_SCHEDULE_FLAT_SECTIONS.map(({ key, label }) => (
            <Tab key={key} $active={activeTab === key} onClick={() => setActiveTab(key)}>
              {label}
            </Tab>
          ))}
        </Tabs>

        <TabPanel $active={activeTab === OBJECTIVES_TAB}>
          {levelOptions.length > 0 && (
            <LevelSelectWrapper>
              <Select
                label={'Objective level'}
                value={currentLevel}
                options={levelOptions}
                isSearchable={false}
                handleChange={(option) => setSelectedLevel(option as { label: string; value: string })}
              />
            </LevelSelectWrapper>
          )}
          {objectiveLevels.map((level) => (
            <TabPanel key={level.id} $active={!!currentLevel && currentLevel.value === level.id}>
              <EntityDefaultsForm
                form={`${PLAN_SCHEDULE_OBJECTIVE_FORM_PREFIX}_${level.id}`}
                initialValues={toFormValues(matchDefault(PLAN_SCHEDULE_ENTITY_TYPE_VALUES.OBJECTIVE, level.id))}
              />
            </TabPanel>
          ))}
        </TabPanel>

        {PLAN_SCHEDULE_FLAT_SECTIONS.map(({ key, entityType, form }) => (
          <TabPanel key={key} $active={activeTab === key}>
            <EntityDefaultsForm form={form} initialValues={toFormValues(matchDefault(entityType, null))} />
          </TabPanel>
        ))}

        <ButtonWrapper>
          <Button type={'button'} layout={BUTTON_LAYOUTS.PURPLE} shadowLess isLoading={isSaving} onClick={handleSave}>
            Save
          </Button>
          <Button type={'button'} layout={BUTTON_LAYOUTS.PURPLE_INVERTED} shadowLess onClick={hideModal}>
            Cancel
          </Button>
        </ButtonWrapper>
      </Paper>
    </Modal>
  )
}

export default PlanScheduleDefaultsModal
